import { useState } from "react"
import { useNavigate } from "react-router-dom"
import FullScreenLayout from "../../../components/FullScreenLayout"
import Loader from "../../../components/Loader"
import { DnaTestTracker } from "../../../utils/api-objects/DnaTestTracker"
import { TrackingStatus } from "../../../utils/Enums"
import { useUpdateDnaTrackerByOrderId } from "../../patients/hooks/useUpdateDnaTrackerByOrderId"
import { useGetDnaTrackersByStatus } from "../hooks/useGetDnaTrackersByStatus"
import { useGetUserById } from "../hooks/useGetUserById"

const statusOptions = [
    { label: "Ritiro Richiesto", value: TrackingStatus.PickupRequested },
    { label: "Partito", value: TrackingStatus.Departed },
    { label: "Arrivato in Lab", value: TrackingStatus.DeliveredToLaboratory },
    { label: "Inizio Estrazione", value: TrackingStatus.StartingToExtract },
]

const handleDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    })
}

const Trackings = () => {
    const navigate = useNavigate()
    const [selectedStatus, setSelectedStatus] = useState<TrackingStatus>(TrackingStatus.PickupRequested)
    const { trackers, isLoading, error, refetch } = useGetDnaTrackersByStatus(selectedStatus)

    const handleBack = () => {
        navigate("/platform-experts/analytics")
    }

    if (isLoading) {
        return (
            <FullScreenLayout title="Trackings" showBackButton={true} onBack={handleBack} backButtonText="Torna a Analytics">
                <div className="w-full h-full flex flex-col items-center justify-center">
                    <div className="text-gray-600">Caricamento trackings...</div>
                </div>
            </FullScreenLayout>
        )
    }

    if (error) {
        return (
            <FullScreenLayout title="Trackings" showBackButton={true} onBack={handleBack} backButtonText="Torna a Analytics">
                <div className="w-full h-full flex flex-col items-center justify-center">
                    <div className="text-red-500">Errore nel caricamento dei trackings: {error}</div>
                </div>
            </FullScreenLayout>
        )
    }

    return (
        <FullScreenLayout title="Trackings" showBackButton={true} onBack={handleBack} backButtonText="Torna a Analytics">
            <div className="w-full flex flex-col space-y-6">
                <div className="flex flex-col md:flex-row md:items-center md:space-x-4 space-y-2 md:space-y-0">
                    <div className="flex flex-wrap gap-2">
                        {statusOptions.map(option => (
                            <button
                                key={option.label}
                                className={`px-4 py-2 rounded border text-sm ${selectedStatus === option.value ? "bg-blue-500 text-white" : "bg-white text-blue-500 border-blue-500"}`}
                                onClick={() => setSelectedStatus(option.value)}
                            >
                                {option.label}
                            </button>
                        ))}
                    </div>
                    <div className="md:ml-auto font-semibold text-gray-700">Totale: {trackers.length}</div>
                </div>
                <div className="space-y-2">
                    {trackers.map((tracker: DnaTestTracker) => (
                        <TrackingCard
                            key={tracker.id}
                            tracker={tracker}
                            refetchTrackers={refetch}
                        />
                    ))}
                    {!isLoading && trackers.length === 0 && (
                        <div className="text-center text-gray-500 py-8">
                            Nessun utente con questo stato trovato.
                        </div>
                    )}
                </div>
            </div>
        </FullScreenLayout>
    )
}

interface TrackingCardProps {
    tracker: DnaTestTracker
    refetchTrackers: () => void
}

const TrackingCard: React.FC<TrackingCardProps> = ({ tracker, refetchTrackers }) => {
    const { user, isLoading, error } = useGetUserById(tracker.user_id)
    const updateDnaTracker = useUpdateDnaTrackerByOrderId()
    const [isUpdating, setIsUpdating] = useState(false)
    const [updateError, setUpdateError] = useState<string | null>(null)

    // Determine next status
    let nextStatus: TrackingStatus | null = null
    if (tracker.tracking_status === TrackingStatus.PickupRequested) {
        nextStatus = TrackingStatus.Departed
    } else if (tracker.tracking_status === TrackingStatus.Departed) {
        nextStatus = TrackingStatus.DeliveredToLaboratory
    } else if (tracker.tracking_status === TrackingStatus.DeliveredToLaboratory) {
        nextStatus = TrackingStatus.StartingToExtract
    }

    const handleUpdateTracker = () => {
        if (!nextStatus) return
        setIsUpdating(true)
        setUpdateError(null)
        updateDnaTracker(
            tracker.order_id,
            nextStatus,
            () => {
                setIsUpdating(false)
                refetchTrackers()
            },
            (err: string) => {
                setIsUpdating(false)
                setUpdateError(err)
            }
        )
    }

    return (
        <div className="p-4 border rounded bg-gray-50">
            {isLoading && <Loader />}
            {error && <div className="text-red-500">{error}</div>}
            {user && (
                <div className="w-full flex flex-col md:flex-row md:justify-between space-y-2 md:space-y-0">
                    <div className="space-y-1">
                        <div><span className="font-semibold">Nome:</span> {user.data?.name}</div>
                        <div><span className="font-semibold">Cognome:</span> {user.data?.family_name}</div>
                        <div><span className="font-semibold">Email:</span> {user.email}</div>
                    </div>
                    <div className="text-sm text-gray-600">
                        Data di {nextStatus === TrackingStatus.Departed ? "richiesta ritiro" : nextStatus === TrackingStatus.DeliveredToLaboratory ? "partenza" : "arrivo in laboratorio"}: {handleDate(tracker.updated_at)}
                    </div>
                </div>
            )}
            {nextStatus && nextStatus !== TrackingStatus.StartingToExtract && (
                <button
                    className="mt-2 px-4 py-2 bg-blue-500 text-white rounded disabled:opacity-50 text-sm"
                    onClick={handleUpdateTracker}
                    disabled={isUpdating}
                >
                    {isUpdating ? "Aggiornamento..." : `Aggiorna a ${nextStatus === TrackingStatus.Departed ? "Ritiro effettuato" : "Arrivato in Lab"}`}
                </button>
            )}
            {updateError && <div className="text-red-500 mt-2 text-sm">{updateError}</div>}
        </div>
    )
}

export default Trackings
